import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { formatTimeRange, formatDateMonthDay, timezoneLabel } from '../../utils/timeFormat';
import { isFlatPay } from '../../utils/payFormat';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft, Plus, Calendar, MapPin, Users, UserPlus, Search, Loader2,
  Archive, ArchiveRestore, Trash2, SlidersHorizontal, X,
  AlertCircle, Layers, CheckSquare,
} from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import UrgencyChips from '../../components/UrgencyChips';
import { groupShiftsByEvent } from '../../hooks/useGroupedEvents';
import GroupedEventCard from '../../components/GroupedEventCard';
import AiCategoryChipStrip from '../../components/AiCategoryChipStrip';
import StaffingIntelligencePanel from '../../components/StaffingIntelligencePanel';
import { classifyShift, summarize as summarizeStaffing } from '../../utils/aiOpsEngine';

const statusColors = {
  open: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-green-100 text-green-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
};

// Iter-224e — bucket tabs in the locked-in user-specified order:
//   1) Active  2) In Progress  3) Ready for Closeout
//   4) Completed  5) Closed  6) Archived
const BUCKETS = [
  { id: 'active',             label: 'Active',             accent: 'cyan'    },
  { id: 'in_progress',        label: 'In Progress',        accent: 'blue'    },
  { id: 'ready_for_closeout', label: 'Ready for Closeout', accent: 'amber'   },
  { id: 'completed',          label: 'Completed',          accent: 'emerald' },
  { id: 'closed',             label: 'Closed',             accent: 'slate'   },
  { id: 'archived',           label: 'Archived',           accent: 'zinc'    },
];

const ACCENT_MAP = {
  cyan:    { active: 'border-cyan-500 text-cyan-700 bg-cyan-50',    badge: 'bg-cyan-100 text-cyan-700'    },
  blue:    { active: 'border-blue-500 text-blue-700 bg-blue-50',    badge: 'bg-blue-100 text-blue-700'    },
  amber:   { active: 'border-amber-500 text-amber-700 bg-amber-50', badge: 'bg-amber-100 text-amber-700'  },
  emerald: { active: 'border-emerald-500 text-emerald-700 bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700' },
  slate:   { active: 'border-slate-500 text-slate-700 bg-slate-50', badge: 'bg-slate-100 text-slate-700'  },
  zinc:    { active: 'border-zinc-500 text-zinc-700 bg-zinc-50',    badge: 'bg-zinc-100 text-zinc-700'    },
};

const AdminShifts = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSuperAdmin, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({
    active: 0, in_progress: 0, ready_for_closeout: 0,
    completed: 0, closed: 0, archived: 0, all: 0,
  });
  // Iter-224e — bucket is URL-synced for deep-linking + bookmarking.
  // Initial value: ?bucket=<id> from the query string, else 'active'.
  const initialBucket = searchParams.get('bucket');
  const validInitial = BUCKETS.some((b) => b.id === initialBucket);
  // Iter-357 (P0-354) — `?bucket=today` is a dashboard convenience alias
  // for "today only". The bucket tabs are unchanged; the date range is
  // pinned to today via date_from/date_to. Any explicit ?date_from/?date_to
  // takes precedence so admin can still deep-link a custom range.
  const bucketTodayAlias = initialBucket === 'today';
  const [bucket, setBucket] = useState(
    bucketTodayAlias ? 'active' : (validInitial ? initialBucket : 'active')
  );
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');

  // Iter-357 (P0-354) — Dashboard-driven attention filter. HomeV2 tiles
  // navigate here with `?attention=<kind>`. We honor these values:
  //   applicants          → shifts with pending applications
  //   unfilled            → shifts where confirmed_count < talent_needed
  //   payroll_pending     → shifts flagged payroll_pending
  //   invoice_pending     → shifts flagged invoice_pending
  //   recap_pending       → shifts flagged recap_pending (or with clock-out
  //                         bookings missing a rating)
  //   clock_out_missing   → routes to /admin/operations?tab=clock-outs
  //                         instead (per-booking view). We surface the
  //                         attention chip and let the user pivot.
  const initialAttention = searchParams.get('attention');

  // Advanced filters — hidden behind a collapsible panel
  const [showFilters, setShowFilters] = useState(false);
  const [roleId, setRoleId] = useState('all');
  const [clientId, setClientId] = useState('all');
  const [stateCode, setStateCode] = useState('all');
  // If ?bucket=today, pin the date range to today.
  const todayIso = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(
    searchParams.get('date_from') || (bucketTodayAlias ? todayIso : '')
  );
  const [dateTo, setDateTo] = useState(
    searchParams.get('date_to') || (bucketTodayAlias ? todayIso : '')
  );
  const [sort, setSort] = useState('newest');
  const [attention, setAttention] = useState(initialAttention || '');

  // Dropdown option sources
  const [roles, setRoles] = useState([]);
  const [clients, setClients] = useState([]);
  const [states, setStates] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [r, c] = await Promise.all([
          api.get('/roles'),
          api.get('/admin/clients/search', { params: { per_page: 500 } })
            .catch(() => ({ data: [] })),
        ]);
        setRoles(Array.isArray(r.data) ? r.data : []);
        const cList = Array.isArray(c.data)
          ? c.data
          : (c.data?.clients || c.data?.results || []);
        setClients(cList);
      } catch {}
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Iter-314d — when a dashboard "attention" chip is active, the
      // count on the tile is computed across ALL buckets (upcoming+past
      // +completed), so restricting this fetch to the current bucket
      // hides the very rows the user tapped to see. Widen the query to
      // `bucket=all` + higher per_page whenever attention is set.
      const attentionActive = !!attention;
      const params = {
        per_page: attentionActive ? 500 : 100,
        sort,
        bucket: attentionActive ? 'all' : bucket,
      };
      if (status && status !== 'all') params.status = status;
      if (roleId && roleId !== 'all') params.role_id = roleId;
      if (clientId && clientId !== 'all') params.client_id = clientId;
      if (stateCode && stateCode !== 'all') params.state = stateCode;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search.trim()) params.search = search.trim();
      const res = await api.get('/admin/shifts', { params });
      setShifts(res.data.shifts || []);
      setTotal(res.data.total || 0);
      if (res.data.counts) setCounts(res.data.counts);
      if (!states.length && res.data.shifts?.length) {
        const uniq = Array.from(new Set(res.data.shifts.map((s) => s.state).filter(Boolean))).sort();
        setStates(uniq);
      }
    } catch (e) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [bucket, status, roleId, clientId, stateCode, dateFrom, dateTo, sort, search, states.length, attention]);

  // Iter-224e — keep ?bucket=<id> in the URL whenever the React state changes
  // so users can bookmark, refresh, or share a specific bucket view.
  useEffect(() => {
    const current = searchParams.get('bucket');
    if (current !== bucket) {
      const next = new URLSearchParams(searchParams);
      next.set('bucket', bucket);
      setSearchParams(next, { replace: true });
    }
  }, [bucket]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, status, roleId, clientId, stateCode, dateFrom, dateTo, sort, attention]);

  const clearFilters = () => {
    setStatus('all');
    setRoleId('all');
    setClientId('all');
    setStateCode('all');
    setDateFrom('');
    setDateTo('');
    setSort('newest');
    setSearch('');
    setAttention('');
    const n = new URLSearchParams(searchParams);
    n.delete('attention');
    n.delete('date_from');
    n.delete('date_to');
    setSearchParams(n, { replace: true });
  };

  const activeFilterCount =
    (status !== 'all' ? 1 : 0) +
    (roleId !== 'all' ? 1 : 0) +
    (clientId !== 'all' ? 1 : 0) +
    (stateCode !== 'all' ? 1 : 0) +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0) +
    (attention ? 1 : 0);

  // Iter-357 (P0-354) — Client-side attention filter on the already-loaded
  // shifts. Uses per-row fields the backend already computes so we don't
  // need to plumb another param through the API. If we ever need
  // server-side counts for these buckets, promote to backend.
  const ATTENTION_LABELS = {
    applicants:        'Applicants Awaiting Review',
    unfilled:          'Jobs Needing Staff',
    payroll_pending:   'Payout Pending',
    invoice_pending:   'Invoice Pending',
    recap_pending:     'Recap Pending',
    clock_out_missing: 'Clock-Out Missing',
    payment_pending:   'Payment Pending',
    ready_for_payment: 'Ready for Payment',
  };
  const applyAttentionFilter = (list) => {
    if (!attention) return list;
    switch (attention) {
      case 'applicants':
        return list.filter(
          (s) => (s.applicant_count || s.applicants_count || 0) > 0,
        );
      case 'unfilled':
        return list.filter(
          (s) => (s.confirmed_count ?? s.staffed_count ?? 0) < (s.talent_needed || 0),
        );
      case 'payroll_pending':
        return list.filter((s) => s.payroll_pending);
      case 'invoice_pending':
        return list.filter((s) => s.invoice_pending);
      case 'recap_pending':
        return list.filter((s) => s.recap_pending);
      // clock_out_missing / payment_pending / ready_for_payment are
      // per-booking states and are drilled into via /admin/operations.
      // Show the chip so the user knows the filter is present but
      // returns the full list (no restrictive filter).
      default:
        return list;
    }
  };
  const filteredShifts = applyAttentionFilter(shifts);

  // Iter-401 (Phase 1 AI Ops) — classify every visible shift once,
  // route through the shared engine so /admin/shifts and /admin/jobs
  // always agree on which shift is "critical", "at risk", etc.
  const aiSummary = useMemo(
    () => summarizeStaffing(filteredShifts, { role: 'admin' }),
    [filteredShifts]
  );

  const [aiCategoryFilter, setAiCategoryFilter] = useState(
    searchParams.get('ai_category') || null,
  );

  const shiftsAfterCategory = useMemo(() => {
    if (!aiCategoryFilter) return filteredShifts;
    return filteredShifts.filter(
      (s) => aiSummary.intelligenceById[s.id]?.category === aiCategoryFilter,
    );
  }, [filteredShifts, aiCategoryFilter, aiSummary]);

  const handleArchive = async (shiftId, e) => {
    e.stopPropagation();
    if (!window.confirm('Archive this shift? It will move to the Archived tab and be hidden from default lists.')) return;
    try {
      await api.patch(`/admin/shifts/${shiftId}/archive`);
      toast.success('Shift archived');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive shift');
    }
  };

  const handleRestore = async (shiftId, e) => {
    e.stopPropagation();
    if (!window.confirm('Restore this shift from Archive? It will reappear in its original bucket.')) return;
    try {
      await api.patch(`/admin/shifts/${shiftId}/unarchive`);
      toast.success('Shift restored');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore shift');
    }
  };

  const isAdmin = ['admin', 'super_admin'].includes(user?.role) || isSuperAdmin();

  // ---- Permanent delete (single + bulk) with assigned-talent warning ----
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteDlg, setDeleteDlg] = useState({
    open: false, ids: [], impact: null, confirmText: '', reason: '', loading: false,
  });

  const toggleSelect = (id) =>
    setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const visibleSingleIds = () =>
    groupShiftsByEvent(shiftsAfterCategory).filter((it) => !it.isGroup).map((s) => s.id);
  const allVisibleSelected = () => {
    const v = visibleSingleIds();
    return v.length > 0 && v.every((id) => selectedIds.includes(id));
  };
  const toggleSelectAll = () => {
    const v = visibleSingleIds();
    setSelectedIds(allVisibleSelected() ? [] : v);
  };
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds([]); };

  const openDeleteDialog = async (ids) => {
    setDeleteDlg({ open: true, ids, impact: null, confirmText: '', reason: '', loading: true });
    try {
      const res = await api.post('/admin/shifts/bulk-delete-impact', { shift_ids: ids });
      setDeleteDlg((d) => ({ ...d, impact: res.data, loading: false }));
    } catch {
      setDeleteDlg((d) => ({
        ...d,
        impact: { items: [], total_assigned_talent: 0, blocked_count: 0, deletable_count: ids.length },
        loading: false,
      }));
    }
  };

  const confirmDeleteNow = async () => {
    const { ids, reason } = deleteDlg;
    setDeleteDlg((d) => ({ ...d, loading: true }));
    try {
      if (ids.length === 1) {
        const res = await api.delete(`/admin/shifts/${ids[0]}`, { params: { reason: reason || undefined } });
        if (res.data?.fallback === 'archived') toast.warning('Delete blocked — shift archived instead.');
        else toast.success(`Deleted${res.data?.notified_talent ? ` · ${res.data.notified_talent} talent notified` : ''}`);
      } else {
        const res = await api.post('/admin/shifts/bulk-delete', {
          shift_ids: ids, confirmation: 'DELETE', reason: reason || null,
        });
        const s = res.data?.summary || {};
        toast.success(`Deleted ${s.deleted || 0} · ${s.notified_talent || 0} talent notified`);
        if (s.blocked) toast.warning(`${s.blocked} skipped (worked/settlement history) — archive those instead.`);
      }
      setDeleteDlg({ open: false, ids: [], impact: null, confirmText: '', reason: '', loading: false });
      exitSelectMode();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
      setDeleteDlg((d) => ({ ...d, loading: false }));
    }
  };

  const handleDelete = (shiftId, e) => { if (e) e.stopPropagation(); openDeleteDialog([shiftId]); };

  const renderPendingBadges = (s) => {
    // Iter-224e — only render the badge strip on Completed / Ready-for-Closeout
    // buckets where these pending states are actionable.
    if (!s.is_in_completed && !s.is_in_ready_for_closeout) return null;
    const badges = [];
    if (s.payroll_pending) badges.push({ key: 'payroll', label: 'Payroll Pending', cls: 'bg-amber-50 text-amber-700 border-amber-300' });
    if (s.invoice_pending) badges.push({ key: 'invoice', label: 'Invoice Pending', cls: 'bg-orange-50 text-orange-700 border-orange-300' });
    if (s.recap_pending)   badges.push({ key: 'recap',   label: 'Recap Pending',   cls: 'bg-rose-50 text-rose-700 border-rose-300' });
    if (badges.length === 0) return null;
    return (
      <div className="flex gap-1 flex-wrap mt-1" data-testid={`pending-badges-${s.id}`}>
        {badges.map((b) => (
          <span
            key={b.key}
            className={`text-[10px] sm:text-xs px-1.5 py-0.5 rounded border font-semibold ${b.cls}`}
            data-testid={`pending-badge-${b.key}-${s.id}`}
          >
            <AlertCircle className="w-3 h-3 inline mr-0.5 -mt-0.5" />
            {b.label}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-shifts-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/admin/home')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          <Button
            onClick={() => navigate('/admin/shifts/create')}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid="create-shift-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Job / Shift
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Jobs / Shifts · Classic</h1>
            <p className="text-sm text-gray-500 mt-1" data-testid="shift-total-line">
              {total} in <span className="font-semibold">{BUCKETS.find((b) => b.id === bucket)?.label}</span>
              {' '}· {counts.all} total across all buckets
              {' '}·{' '}
              <button
                onClick={() => navigate('/admin/shifts')}
                data-testid="back-to-jobs-v2"
                className="text-cyan-700 hover:underline font-semibold"
              >
                Back to event view
              </button>
            </p>
            {/* Iter-357 (P0-354) — Active attention/today filter chips.
                Signals to the operator that the list is filtered from a
                dashboard drilldown; clicking clears the filter. */}
            {(attention || bucketTodayAlias) && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="attention-chip-strip">
                {bucketTodayAlias && (
                  <button
                    onClick={() => { setDateFrom(''); setDateTo(''); const n = new URLSearchParams(searchParams); n.delete('bucket'); setSearchParams(n, { replace: true }); }}
                    data-testid="chip-bucket-today"
                    className="inline-flex items-center gap-1 rounded-full border border-cyan-300 bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-800 hover:bg-cyan-100"
                  >
                    Today only <X className="w-3 h-3" />
                  </button>
                )}
                {attention && (
                  <button
                    onClick={() => { setAttention(''); const n = new URLSearchParams(searchParams); n.delete('attention'); setSearchParams(n, { replace: true }); }}
                    data-testid={`chip-attention-${attention}`}
                    className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-800 hover:bg-rose-100"
                  >
                    {ATTENTION_LABELS[attention] || attention} <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()}
                placeholder="Search venue / city / client / role"
                className="pl-9 w-64"
                data-testid="shift-search-input"
              />
            </div>
            <Button variant="outline" onClick={load} data-testid="search-btn">Search</Button>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-36" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="oldest">Oldest first</SelectItem>
                <SelectItem value="recently_created">Recently created</SelectItem>
                <SelectItem value="earliest_shift">Earliest shift</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={showFilters ? 'default' : 'outline'}
              onClick={() => setShowFilters((v) => !v)}
              data-testid="toggle-filters-btn"
              className={showFilters ? 'bg-cyan-500 hover:bg-cyan-600 text-white' : ''}
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-2 bg-white text-cyan-700 px-1.5 py-0 text-xs">{activeFilterCount}</Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Iter-224e — bucket tab strip (horizontal scroll on mobile) */}
        <div
          className="mb-4 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"
          data-testid="shift-bucket-tabs"
        >
          {BUCKETS.map((b) => {
            const isActive = bucket === b.id;
            const accent = ACCENT_MAP[b.accent] || ACCENT_MAP.cyan;
            const cnt = counts[b.id] || 0;
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => setBucket(b.id)}
                data-testid={`bucket-tab-${b.id}`}
                aria-selected={isActive}
                className={`shrink-0 px-3 py-2 rounded-lg border-2 text-sm font-semibold transition whitespace-nowrap flex items-center gap-2 ${
                  isActive
                    ? accent.active
                    : 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50'
                }`}
              >
                <span>{b.label}</span>
                <span
                  className={`min-w-[1.5rem] text-center px-1.5 py-0.5 rounded text-xs font-bold ${
                    isActive ? accent.badge : 'bg-gray-100 text-gray-600'
                  }`}
                  data-testid={`bucket-count-${b.id}`}
                >
                  {cnt}
                </span>
              </button>
            );
          })}
        </div>

        {/* Iter-401 (Phase 1 AI Ops) — AI category chip strip. Same engine
            powers /admin/jobs so classification is consistent across pages. */}
        <div className="mb-1">
          <div className="flex items-center gap-2 mb-1.5 px-1">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-700">AI Priorities</span>
            <span className="text-xs text-slate-400">— tap to filter · {aiSummary.totals.attention} shifts need attention</span>
          </div>
          <AiCategoryChipStrip
            countsByCategory={aiSummary.countsByCategory}
            active={aiCategoryFilter}
            onSelect={(cat) => {
              setAiCategoryFilter(cat);
              const n = new URLSearchParams(searchParams);
              if (cat) n.set('ai_category', cat); else n.delete('ai_category');
              setSearchParams(n, { replace: true });
            }}
          />
        </div>

        {/* Advanced filter panel — collapsible */}
        {showFilters && (
          <Card className="mb-4 border-cyan-200 bg-cyan-50/30" data-testid="filter-panel">
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Role</Label>
                  <Select value={roleId} onValueChange={setRoleId}>
                    <SelectTrigger data-testid="role-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All roles</SelectItem>
                      {roles.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Client</Label>
                  <Select value={clientId} onValueChange={setClientId}>
                    <SelectTrigger data-testid="client-filter"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="all">All clients</SelectItem>
                      {clients
                        .filter((c) => (c.id || c.user_id))
                        .map((c) => (
                          <SelectItem key={c.id || c.user_id} value={c.id || c.user_id}>
                            {c.display || c.company_name || c.contact_name || c.email
                              || `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Client'}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">State</Label>
                  <Select value={stateCode} onValueChange={setStateCode}>
                    <SelectTrigger data-testid="state-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All states</SelectItem>
                      {states.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">From</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    data-testid="date-from-filter"
                  />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    data-testid="date-to-filter"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-3 gap-2">
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="w-4 h-4 mr-1" />
                  Clear all
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {isAdmin && bucket !== 'archived' && !loading && shiftsAfterCategory.length > 0 && (
          <div className="flex items-center justify-between gap-2 mb-2" data-testid="bulk-delete-toolbar">
            <Button
              variant={selectMode ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
              data-testid="toggle-select-mode-btn"
            >
              <CheckSquare className="w-4 h-4 mr-1" />
              {selectMode ? 'Done selecting' : 'Select to delete'}
            </Button>
            {selectMode && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{selectedIds.length} selected</span>
                <Button variant="ghost" size="sm" onClick={toggleSelectAll} data-testid="select-all-btn">
                  {allVisibleSelected() ? 'Clear all' : 'Select all'}
                </Button>
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={selectedIds.length === 0}
                  onClick={() => openDeleteDialog(selectedIds)}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete {selectedIds.length || ''}
                </Button>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : shiftsAfterCategory.length === 0 ? (
          <Card className="bg-white border-gray-200" data-testid="shifts-empty-state">
            <CardContent className="py-16 text-center text-gray-500">
              No shifts
              {attention ? (
                <> matching <span className="font-semibold">{ATTENTION_LABELS[attention] || attention}</span></>
              ) : (
                <> in <span className="font-semibold">{BUCKETS.find((b) => b.id === bucket)?.label}</span></>
              )}
              .
              {attention && (
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setAttention(''); const n = new URLSearchParams(searchParams); n.delete('attention'); setSearchParams(n, { replace: true }); }}
                    data-testid="clear-attention-btn"
                  >
                    <X className="w-4 h-4 mr-1" /> Clear filter
                  </Button>
                </div>
              )}
              {bucket === 'active' && !attention && (
                <div className="mt-4">
                  <Button
                    onClick={() => navigate('/admin/shifts/create')}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create a new shift
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {groupShiftsByEvent(shiftsAfterCategory).map((item) => {
              if (!item.isGroup) {
                const s = item;
                return (
              <Card
                key={s.id}
                className="bg-white border-gray-200 hover:border-cyan-400 hover:shadow-sm transition cursor-pointer overflow-hidden"
                onClick={() => (selectMode ? toggleSelect(s.id) : navigate(`/admin/shifts/${s.id}`))}
                data-testid="shift-row"
              >
                <CardContent className="p-3 sm:p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
                    {selectMode && (
                      <div onClick={(e) => e.stopPropagation()} className="flex items-center pt-1">
                        <Checkbox
                          checked={selectedIds.includes(s.id)}
                          onCheckedChange={() => toggleSelect(s.id)}
                          data-testid={`select-shift-${s.id}`}
                        />
                      </div>
                    )}
                    <div className="flex flex-col items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-cyan-50 rounded-lg border border-cyan-200 shrink-0">
                      <Calendar className="w-4 h-4 text-cyan-600" />
                      <span className="text-[10px] sm:text-xs font-semibold text-cyan-700 mt-0.5">
                        {formatDateMonthDay(s.shift_date)}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 break-words">{s.event_title || s.venue_name || '(no venue name)'}</h3>
                        <Badge className={`${statusColors[s.status] || 'bg-gray-100 text-gray-700'} text-xs shrink-0`}>{s.status}</Badge>
                        {s.series_total > 1 && (
                          <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200 text-xs shrink-0 gap-1" data-testid={`shift-series-badge-${s.id}`}>
                            <Layers className="w-3 h-3" /> Day {s.series_day_index} of {s.series_total}
                          </Badge>
                        )}
                        <UrgencyChips urgency={s.urgency} fillConfidence={s.fill_confidence} />
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-x-3 gap-y-1 flex-wrap mt-1">
                        <span className="font-medium text-gray-700">{s.role_name}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" />{s.city}, {s.state}</span>
                        <span>{formatTimeRange(s.start_time, s.end_time)}</span>
                        {s.timezone && (
                          <span className="text-xs text-gray-400" data-testid={`shift-tz-${s.id}`}>({timezoneLabel(s.timezone)})</span>
                        )}
                        <span className="text-gray-400 break-all">· {s.client_display}</span>
                      </div>
                      {renderPendingBadges(s)}
                      {s.series_total > 1 && s.series_dates && (
                        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`shift-series-dates-${s.id}`} onClick={(e) => e.stopPropagation()}>
                          <Layers className="w-3 h-3 text-indigo-400 shrink-0" />
                          <span className="text-[11px] text-gray-400 mr-0.5">Multi-day series:</span>
                          {s.series_dates.map((d) => (
                            d.is_current ? (
                              <span key={d.shift_id} className="text-[11px] font-semibold bg-indigo-600 text-white rounded px-1.5 py-0.5" title="This day">
                                {formatDateMonthDay(d.shift_date)}
                              </span>
                            ) : (
                              <button
                                key={d.shift_id}
                                type="button"
                                onClick={() => navigate(`/admin/shifts/${d.shift_id}`)}
                                className="text-[11px] bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 rounded px-1.5 py-0.5 transition-colors"
                                title="Open this day"
                              >
                                {formatDateMonthDay(d.shift_date)}
                              </button>
                            )
                          ))}
                        </div>
                      )}
                      {/* Iter-401 (Phase 1 AI Ops) — self-explaining copy */}
                      {aiSummary.intelligenceById[s.id] && (
                        <StaffingIntelligencePanel
                          intel={aiSummary.intelligenceById[s.id]}
                          compact
                          showActions={false}
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-4 text-sm flex-wrap shrink-0">
                    <div className="flex items-center gap-1 text-gray-600" title="Staffed">
                      <Users className="w-4 h-4" />
                      <span className="font-semibold">{s.staffed_count ?? s.confirmed_count ?? 0}/{s.talent_needed}</span>
                      <span className="text-gray-400 text-xs">staffed</span>
                    </div>
                    {(s.backups_needed || 0) > 0 && (
                      <div
                        className="flex items-center gap-2 text-xs"
                        data-testid={`pb-counts-${s.id}`}
                        title={`${s.primary_count || 0} Primary · ${s.backup_count || 0} Backup`}
                      >
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold">
                          P {s.primary_count || 0}/{s.talent_needed || 0}
                        </span>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800 font-semibold">
                          B {s.backup_count || 0}/{s.backups_needed || 0}
                        </span>
                      </div>
                    )}
                    {(s.applicant_count || 0) > 0 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/admin/shifts/${s.id}?drawer=applicants`); }}
                        className="flex items-center gap-1 text-indigo-700 hover:text-indigo-900 hover:underline"
                        data-testid={`applicant-count-${s.id}`}
                        title={`${s.applicant_count} pending applicant${s.applicant_count === 1 ? '' : 's'}`}
                      >
                        <UserPlus className="w-4 h-4" />
                        <span className="font-semibold">{s.applicant_count}</span>
                        <span className="text-xs">applicant{s.applicant_count === 1 ? '' : 's'}</span>
                      </button>
                    )}
                    {isFlatPay(s) ? (
                      <div className="text-gray-700 font-medium">${s.flat_pay.toFixed(2)} flat</div>
                    ) : s.bill_rate && (
                      <div className="text-gray-700 font-medium">${s.bill_rate.toFixed(2)}/hr</div>
                    )}
                    {/* Iter-224e — Archive on non-archived buckets, Restore on Archived */}
                    {bucket === 'archived' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-8 px-2"
                        onClick={(e) => handleRestore(s.id, e)}
                        data-testid={`restore-shift-${s.id}`}
                        title="Restore from archive"
                      >
                        <ArchiveRestore className="w-3.5 h-3.5" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-200 text-amber-700 hover:bg-amber-50 h-8 px-2"
                        onClick={(e) => handleArchive(s.id, e)}
                        data-testid={`archive-shift-${s.id}`}
                        title="Archive shift"
                      >
                        <Archive className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50 h-8 px-2"
                        onClick={(e) => handleDelete(s.id, e)}
                        data-testid={`delete-shift-${s.id}`}
                        title="Delete permanently"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
                );
              }
              // Iter-310 / Iter-310b — Multi-role grouped event card.
              return (
                <GroupedEventCard
                  key={item.key}
                  group={item}
                  audience="admin"
                  defaultExpanded
                  onOpenEvent={(g) => navigate(`/admin/shifts/${g.primary_shift_id}`)}
                  onOpenRole={(r) => navigate(`/admin/shifts/${r.id}`)}
                  onOpenApplicants={(r) => navigate(`/admin/shifts/${r.id}?drawer=applicants`)}
                  renderRoleExtras={(r) => (
                    <>
                      {bucket === 'archived' ? (
                        <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-7 px-2"
                          onClick={(e) => handleRestore(r.id, e)} data-testid={`restore-shift-${r.id}`} title="Restore from archive">
                          <ArchiveRestore className="w-3.5 h-3.5" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="border-amber-200 text-amber-700 hover:bg-amber-50 h-7 px-2"
                          onClick={(e) => handleArchive(r.id, e)} data-testid={`archive-shift-${r.id}`} title="Archive shift">
                          <Archive className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {isAdmin && (
                        <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 h-7 px-2"
                          onClick={(e) => handleDelete(r.id, e)} data-testid={`delete-shift-${r.id}`} title="Delete permanently">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </>
                  )}
                />
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={deleteDlg.open} onOpenChange={(o) => !o && setDeleteDlg((d) => ({ ...d, open: false }))}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              Permanently delete {deleteDlg.ids.length > 1 ? `${deleteDlg.ids.length} jobs` : 'this job'}?
            </DialogTitle>
            <DialogDescription>
              This cannot be undone. Jobs with worked time, approved timesheets, or settlement
              history are protected and will be skipped (archive those instead).
            </DialogDescription>
          </DialogHeader>
          {deleteDlg.loading && !deleteDlg.impact ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
          ) : (
            <div className="space-y-3 text-sm">
              {deleteDlg.impact && deleteDlg.impact.total_assigned_talent > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-800 p-3" data-testid="assigned-talent-warning">
                  <b>{deleteDlg.impact.total_assigned_talent} talent still assigned.</b> Deleting will
                  cancel their assignment(s) and automatically notify each of them. Choose “Go back”
                  if you’d rather remove the talent manually first.
                </div>
              )}
              {deleteDlg.impact && deleteDlg.impact.blocked_count > 0 && (
                <div className="rounded-md border border-slate-300 bg-slate-50 text-slate-700 p-3">
                  {deleteDlg.impact.blocked_count} selected job(s) have worked/settlement history and
                  will be skipped.
                </div>
              )}
              <div>
                <Label className="text-xs">Reason (recorded in the audit log)</Label>
                <Textarea
                  value={deleteDlg.reason}
                  onChange={(e) => setDeleteDlg((d) => ({ ...d, reason: e.target.value }))}
                  placeholder="e.g. duplicate of corrected posting"
                  rows={2}
                  data-testid="delete-reason-input"
                />
              </div>
              <div>
                <Label className="text-xs">Type DELETE to confirm</Label>
                <Input
                  value={deleteDlg.confirmText}
                  onChange={(e) => setDeleteDlg((d) => ({ ...d, confirmText: e.target.value }))}
                  placeholder="DELETE"
                  data-testid="delete-confirm-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDlg((d) => ({ ...d, open: false }))} data-testid="delete-cancel-btn">
              Go back
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={deleteDlg.loading || deleteDlg.confirmText !== 'DELETE'}
              onClick={confirmDeleteNow}
              data-testid="delete-confirm-submit-btn"
            >
              {deleteDlg.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminShifts;
